import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiGet, apiSend } from '../lib/api'
import type { Me, Progress, Task } from '../lib/types'

/**
 * Цель и прогресс — те же два состояния, что и на экране цели в боте
 * (app/handlers/goal.py + utils/scoring.py), и переключаются они на одном
 * событии: readiness.ready из движка.
 *
 *  • оценка не открыта — «Путь к цели»: два шага со счётчиками и одно
 *    действие «решать дальше». Никаких баллов: пока заданий затронуто мало,
 *    любая цифра была бы приором (0,45 × максимум), то есть одной и той же
 *    константой для любого новичка (правило §2.23);
 *  • оценка открыта — балл на шкале экзамена, карта заданий и слабые места.
 *
 * Шкала идёт от нуля до максимума экзамена, а не «от нуля до цели»: положение
 * ученика — это его отметка, а не процент выполнения плана. Прежняя полоса-доля
 * упиралась в конец уже на приоре и рапортовала о взятой цели раньше, чем
 * ученик открывал первое задание.
 */
export default function ProgressScreen({ me }: { me: Me }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // Цель ставится в два шага, как в боте (goal.py: goal:grade → goal:save):
  // выбранная отметка ждёт здесь, пока ученик не назовёт срок. Срок — не
  // мелочь оформления: от него зависит, к какой дате мы считаем разрыв, и
  // молча ставить «до экзамена» одиннадцатикласснику и семикласснику одинаково
  // неверно.
  const [выбраннаяЦель, setВыбраннаяЦель] = useState<number | null>(null)

  const { data, isLoading } = useQuery<Progress>({
    queryKey: ['progress'],
    queryFn: () => apiGet('/api/progress'),
  })

  async function поставитьЦель(grade: number, deadline: string) {
    setBusy(true)
    setError('')
    try {
      await apiSend('/api/goal', 'PUT', { grade, deadline })
      await queryClient.invalidateQueries({ queryKey: ['progress'] })
      setВыбраннаяЦель(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось сохранить цель.')
    } finally {
      setBusy(false)
    }
  }

  // Смена экзамена — та же кнопка, что в боте на экране цели («🔀 Экзамен: X»,
  // goal.py): в один шаг вместо двух, потому что вариантов всего два и второй
  // экран бота (_exam_picker_markup) здесь ничего не добавляет.
  async function сменитьЭкзамен(slug: string) {
    setBusy(true)
    setError('')
    try {
      await apiSend('/api/me', 'PATCH', { exam: slug })
      await queryClient.invalidateQueries()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось сменить экзамен.')
    } finally {
      setBusy(false)
    }
  }

  async function решать(body: Record<string, unknown>) {
    setBusy(true)
    setError('')
    try {
      const task = await apiSend<Task>('/api/tasks/next', 'POST', body)
      queryClient.setQueryData(['task', 'current'], { task })
      navigate('/task')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не получилось взять задачу.')
    } finally {
      setBusy(false)
    }
  }

  // В урезанном режиме показа подбор оркестратором отключён и в API
  // (routes/tasks.py), поэтому там ведём в каталог — выбрать раздел руками.
  const решатьДальше = () => (me.mvp_mode ? navigate('/') : решать({ mode: 'study' }))

  if (isLoading || !data) {
    return (
      <>
        <h1>Цель и прогресс</h1>
        <div className="skeleton" />
        <div className="skeleton" />
      </>
    )
  }

  const { estimate, goal, exam, readiness, scale } = data

  return (
    <>
      <h1>Цель и прогресс</h1>

      {error && <div className="error">{error}</div>}

      <div className="card">
        <div className="row">
          <b>Цель</b>
          <span className="spacer" />
          <span className="badge">{exam.code}</span>
        </div>

        {!goal ? (
          <p className="muted" style={{ margin: '8px 0 0' }}>
            Поставь цель — и я покажу, на сколько ты сдаёшь сейчас и сколько
            осталось до неё.
          </p>
        ) : (
          <>
            {/* Только то, что ученик реально понимает — отметку/тестовый
                балл. Первичные баллы (внутренняя единица подсчёта) на этой
                строке не нужны: они всё равно подписаны на шкале ниже. */}
            <p style={{ fontSize: 22, fontWeight: 700, margin: '8px 0 0' }}>
              {goal.target_text}
            </p>
            {goal.deadline_line && (
              <p className="small muted" style={{ margin: '2px 0 0' }}>
                {goal.deadline_line}
              </p>
            )}
          </>
        )}

        {/* Шаг 1 — результат. Шаг 2 (срок) открывается под ним, а не на
            отдельном экране: в боте это второе сообщение, здесь достаточно
            показать выбор ниже — путь тот же, лишнего перехода нет. */}
        <div className="row-wrap" style={{ marginTop: 14 }}>
          {exam.grade_targets.map((g) => (
            <button
              key={g}
              className={`btn${(выбраннаяЦель ?? goal?.target_grade) === g ? ' btn-primary' : ''}`}
              disabled={busy}
              onClick={() => setВыбраннаяЦель(g)}
            >
              на {g}
            </button>
          ))}
        </div>

        {выбраннаяЦель !== null && (
          <div style={{ marginTop: 14 }}>
            <p className="small muted" style={{ margin: '0 0 8px' }}>
              К какому сроку готовимся?
            </p>
            <div className="list">
              <button
                className="list-item"
                disabled={busy}
                onClick={() => поставитьЦель(выбраннаяЦель, 'exam')}
              >
                <span className="title">📅 {exam.deadline_label}</span>
              </button>
              <button
                className="list-item"
                disabled={busy}
                onClick={() => поставитьЦель(выбраннаяЦель, 'q')}
              >
                <span className="title">⏳ Через 3 месяца</span>
              </button>
              <button
                className="list-item"
                disabled={busy}
                onClick={() => поставитьЦель(выбраннаяЦель, 'none')}
              >
                <span className="title">♾ Без срока</span>
              </button>
            </div>
            <button
              className="btn"
              style={{ marginTop: 8 }}
              disabled={busy}
              onClick={() => setВыбраннаяЦель(null)}
            >
              Отмена
            </button>
          </div>
        )}
      </div>

      {goal && !data.ready && (
        <PathCard
          goal={goal.target_text}
          readiness={readiness}
          busy={busy}
          onSolve={решатьДальше}
        />
      )}

      {goal && data.ready && estimate && (
        <>
          <div className="card">
            <div className="row">
              <b>Сейчас</b>
              <span className="spacer" />
              {data.trend && <span className="small muted">{data.trend}</span>}
            </div>
            <p style={{ fontSize: 20, fontWeight: 700, margin: '8px 0 2px' }}>
              {estimate.grade_text}
            </p>
            {exam.scope_note && <p className="small muted">{exam.scope_note}</p>}

            <ExamScale scale={scale} />

            {data.gap_text && (
              <p className="small" style={{ margin: '10px 0 0' }}>{data.gap_text}</p>
            )}
          </div>

          <div className="card">
            <div className="row">
              <b>Карта заданий</b>
              <span className="spacer" />
              <span className="small muted">
                проверено заданий: {data.coverage.checked} из {data.coverage.total}
              </span>
            </div>
            <div className="kim-map">
              {data.positions.map((p) => (
                <button
                  key={p.position}
                  className={`kim-cell kim-${p.state}`}
                  disabled={busy}
                  title={
                    p.attempts
                      ? `Задание ${p.position}: ${p.max} б, решено ${p.attempts}`
                      : `Задание ${p.position}: ${p.max} б, ещё не проверено`
                  }
                  onClick={() => решать({ mode: 'kim', kim_positions: [p.position] })}
                >
                  {p.position}
                </button>
              ))}
            </div>
            <p className="small muted" style={{ margin: '10px 0 0' }}>
              <span className="kim-dot kim-strong" /> берёшь
              {' · '}
              <span className="kim-dot kim-weak" /> теряешь
              {' · '}
              <span className="kim-dot kim-unknown" /> не проверено
            </p>
          </div>

          {data.weak.length > 0 && (
            <div className="card">
              <b>Где теряются баллы</b>
              <div className="list" style={{ marginTop: 10 }}>
                {data.weak.map((w) => (
                  <button
                    key={w.position}
                    className="list-item"
                    disabled={busy}
                    onClick={() => решать({ mode: 'kim', kim_positions: [w.position] })}
                  >
                    <span className="title">
                      №{w.position}{w.name ? ` ${w.name}` : ''}
                    </span>
                    <span className="meta">
                      берёшь {формат(w.current)} из {w.max}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <div className="card">
        <b>Экзамен</b>
        <div className="row-wrap" style={{ marginTop: 10 }}>
          {me.exams.map((e) => (
            <button
              key={e.slug}
              className={`btn${e.code === me.exam.code ? ' btn-primary' : ''}`}
              disabled={busy || e.code === me.exam.code}
              onClick={() => сменитьЭкзамен(e.slug)}
            >
              {e.code}
            </button>
          ))}
        </div>
      </div>

      <div className="row-wrap" style={{ marginTop: 4 }}>
        <button className="btn" onClick={() => navigate('/achievements?from=goal')}>Достижения</button>
        <button className="btn" onClick={() => navigate('/')}>Главная</button>
      </div>
    </>
  )
}

/** Балл для показа: «1» вместо «1.0», «1,5» вместо «1.5» — как _format_points в боте. */
function формат(value: number): string {
  const rounded = Math.round(value * 10) / 10
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(1).replace('.', ',')
}

/** Путь к оценке: два шага со счётчиками и одно действие. */
function PathCard({
  goal, readiness, busy, onSolve,
}: {
  goal: string
  readiness: Progress['readiness']
  busy: boolean
  onSolve: () => void
}) {
  const шаг1 = readiness.attempts >= readiness.attempts_needed
  const шаг2 = readiness.checked >= readiness.checked_needed
  return (
    <div className="card">
      <b>Путь к цели: {goal}</b>

      <Step
        done={шаг1}
        number={1}
        title={`Решить ${readiness.attempts_needed} задач`}
        value={readiness.attempts}
        total={readiness.attempts_needed}
      />
      <Step
        done={шаг2}
        number={2}
        title="Задачи из разных заданий экзамена"
        value={readiness.checked}
        total={readiness.checked_needed}
      />
      <div className="step">
        <span className="step-num">3</span>
        <div className="step-body">
          <div className="step-title muted">
            Твоя отметка и слабые места — откроются после шагов 1 и 2
          </div>
        </div>
      </div>

      <button
        className="btn btn-primary btn-block btn-lg"
        style={{ marginTop: 12 }}
        disabled={busy}
        onClick={onSolve}
      >
        ▶️ Решать задачи
      </button>
    </div>
  )
}

/** Шаг пути: точки заполняются по мере движения — как ●○ в боте. */
function Step({
  done, number, title, value, total,
}: {
  done: boolean
  number: number
  title: string
  value: number
  total: number
}) {
  const сделано = Math.max(0, Math.min(total, value))
  return (
    <div className="step">
      <span className={`step-num${done ? ' step-done' : ''}`}>{done ? '✓' : number}</span>
      <div className="step-body">
        <div className="step-title">{title}</div>
        <div className="step-dots">
          {Array.from({ length: total }, (_, i) => (
            <i key={i} className={i < сделано ? 'on' : ''} />
          ))}
          <span className="small muted">{сделано} из {total}</span>
        </div>
      </div>
    </div>
  )
}

/**
 * Шкала экзамена: зоны сверху, ось, метка цели, метка «ты».
 * Порядок строк и сегменты — те же, что у format_scale в боте.
 */
function ExamScale({ scale }: { scale: Progress['scale'] }) {
  const max = scale.max_primary || 1

  // Метка стоит по своему значению, но у краёв шкалы прижимается внутрь —
  // иначе «▲ ты» на нуле или на максимуме уезжает за границу карточки.
  function положение(points: number) {
    const pct = Math.max(0, Math.min(100, (points / max) * 100))
    const transform =
      pct < 8 ? 'none' : pct > 92 ? 'translateX(-100%)' : 'translateX(-50%)'
    return { left: `${pct}%`, transform }
  }

  return (
    <div className="scale" style={{ marginTop: 12 }}>
      <div className="scale-track">
        {scale.segments.map((s, i) => {
          // Зона, в которой ученик сейчас, подсвечена: метка под осью говорит
          // «где», а заливка — «в какой ты зоне», и это видно с одного взгляда.
          const последний = i === scale.segments.length - 1
          const тут =
            scale.current !== null &&
            scale.current >= s.from &&
            (последний ? scale.current <= s.to : scale.current < s.to)
          return (
            <div
              key={s.from}
              className={`scale-seg${тут ? ' scale-seg-now' : ''}`}
              style={{ flexGrow: s.to - s.from }}
            >
              <span className="scale-label">{s.label}</span>
            </div>
          )
        })}
      </div>
      {/* Метки — на разных строках: рядом они наползали бы друг на друга ровно
          тогда, когда ученик подошёл к цели вплотную. Порядок тот же, что в боте. */}
      {scale.target !== null && (
        <div className="scale-row">
          <span className="scale-mark scale-target" style={положение(scale.target)}>
            ★ цель
          </span>
        </div>
      )}
      {scale.current !== null && (
        <div className="scale-row">
          <span className="scale-mark scale-you" style={положение(scale.current)}>
            ▲ ты
          </span>
        </div>
      )}
    </div>
  )
}
